import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { configService } from '@/services/config.service'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import logo from '@/assets/logo.svg'
// WebP of hero.png — the 2.2 MB PNG exceeds the PWA precache limit.
import hero from '@/assets/images/hero.webp'
import song from '@/assets/music/u-like-it.mp3'

/** Example scenarios shown in the earnings calculator: merchants won per month. */
const EXAMPLE_MERCHANTS = [5, 10, 20]

function CheckIcon({ className = '' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z"
        clipRule="evenodd"
      />
    </svg>
  )
}

/** Renders a euro amount, or a pulsing placeholder while the config is still loading. */
function Amount({ value, className = '' }: { value?: number; className?: string }) {
  if (value === undefined) {
    return <span className={`inline-block h-[0.8em] w-[2.2ch] rounded bg-current opacity-20 animate-pulse ${className}`} />
  }
  return <span className={className}>{value}</span>
}

function YouTubeLazy({ videoId, title, onPlay }: { videoId: string; title: string; onPlay: () => void }) {
  const [started, setStarted] = useState(false)

  if (!started) {
    return (
      <div
        className="aspect-video rounded-lg overflow-hidden shadow-lg relative cursor-pointer group"
        onClick={() => { onPlay(); setStarted(true) }}
      >
        <img
          src={`https://img.youtube.com/vi/${videoId}/hqdefault.jpg`}
          alt={title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
          <svg className="w-16 h-16 text-white opacity-90" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
    )
  }

  return (
    <div className="aspect-video rounded-lg overflow-hidden shadow-lg">
      <iframe
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
      />
    </div>
  )
}

export function HomePage() {
  const { t } = useTranslation()
  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)

  const { data: compensationConfig } = useQuery({
    queryKey: ['config', 'compensation'],
    queryFn: () => configService.getCompensationConfig(),
    staleTime: 60_000,
  })

  const hourly = compensationConfig?.hourlyRate
  const bonus = compensationConfig?.bonusPerRegistration
  const perMerchant = hourly !== undefined && bonus !== undefined ? hourly + bonus : undefined

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.src = song
      audioRef.current.loop = true
    }
  }, [])

  const toggleMusic = () => {
    if (!audioRef.current) return
    if (isPlaying) {
      audioRef.current.pause()
      setIsPlaying(false)
    } else {
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {})
    }
  }

  const pauseMusic = () => {
    if (audioRef.current) {
      audioRef.current.pause()
      setIsPlaying(false)
    }
  }

  return (
    <div className="min-h-screen bg-white">
      <audio ref={audioRef} />

      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 py-6 flex justify-between items-center">
        <a href="https://u-like-it.de" target="_blank" rel="noopener noreferrer">
          <img src={logo} alt="U-Like-It" className="h-12 w-auto hover:opacity-80 transition-opacity cursor-pointer" />
        </a>
        <div className="flex items-center gap-3">
          <button
            onClick={toggleMusic}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            title={isPlaying ? t('home.disableMusic') : t('home.enableMusic')}
          >
            {!isPlaying ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-[#107bd4]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
            )}
          </button>
          <LanguageSwitcher />
        </div>
      </div>

      {/* Hero: what's in it for me, in 3 seconds */}
      <section className="relative">
        {/* Split layout: the photo stays unobscured so the scene reads at a glance. */}
        <div className="bg-[#00204f] flex flex-col lg:grid lg:grid-cols-2 lg:items-stretch">
          <div className="relative order-2 lg:order-1 flex items-center justify-center lg:justify-end px-4 sm:px-6 lg:pl-10 lg:pr-12 py-14 lg:py-20">
            <div className="w-full max-w-xl lg:max-w-[34rem]">
              <p className="text-sm font-semibold uppercase tracking-wider text-[#ffb37a] mb-4">
                {t('home.hero.eyebrow')}
              </p>
              <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-5">
                {t('home.hero.titleBefore')}{' '}
                <span className="text-[#ff8a3d] whitespace-nowrap">
                  <Amount value={perMerchant} /> €
                </span>{' '}
                {t('home.hero.titleAfter')}
              </h1>
              <p className="text-lg md:text-xl text-blue-50 mb-8">
                {t('home.hero.subtitle')}
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-8">
                <a
                  href="/register"
                  className="bg-[#ff5801] hover:bg-[#e64f00] text-white font-semibold rounded-lg px-8 py-4 text-center text-lg transition-colors shadow-lg"
                >
                  {t('home.hero.ctaPrimary')}
                </a>
                <a
                  href="#verdienst"
                  className="bg-white/10 hover:bg-white/20 border border-white/40 text-white font-semibold rounded-lg px-8 py-4 text-center text-lg transition-colors"
                >
                  {t('home.hero.ctaSecondary')}
                </a>
              </div>

              <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-blue-50">
                {[1, 2, 3].map((i) => (
                  <li key={i} className="flex items-center gap-1.5">
                    <CheckIcon className="w-4 h-4 text-[#ff8a3d] shrink-0" />
                    {t(`home.hero.badge${i}`)}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="order-1 lg:order-2 relative">
            <img
              src={hero}
              alt={t('home.hero.imageAlt')}
              className="w-full h-64 sm:h-80 lg:h-full lg:absolute lg:inset-0 object-cover object-center"
            />
            {/* Blends the photo's left edge into the text panel on wide screens. */}
            <div className="hidden lg:block absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#00204f] to-transparent" />
          </div>
        </div>
      </section>

      {/* 1. Was verdienst du? */}
      <section id="verdienst" className="max-w-5xl mx-auto px-4 py-16 scroll-mt-4">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-3">
          {t('home.earnings.heading')}
        </h2>
        <p className="text-lg text-center text-gray-600 mb-10">
          {t('home.earnings.intro')}
        </p>

        <div className="grid md:grid-cols-2 gap-5 mb-6">
          <div className="rounded-xl p-7 bg-[#107bd4]/8 border border-[#107bd4]/25 text-center">
            <div className="text-5xl font-bold text-[#024ab0] mb-2">
              <Amount value={hourly} /> €
            </div>
            <p className="font-semibold text-gray-900">{t('home.earnings.hourlyLabel')}</p>
            <p className="text-sm text-gray-600 mt-1">{t('home.earnings.hourlyHint')}</p>
          </div>
          <div className="rounded-xl p-7 bg-[#ff5801]/8 border border-[#ff5801]/25 text-center">
            <div className="text-5xl font-bold text-[#ff5801] mb-2">
              +<Amount value={bonus} /> €
            </div>
            <p className="font-semibold text-gray-900">{t('home.earnings.bonusLabel')}</p>
            <p className="text-sm text-gray-600 mt-1">{t('home.earnings.bonusHint')}</p>
          </div>
        </div>

        <div className="rounded-xl border border-gray-200 p-7 bg-white shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">
            {t('home.earnings.calcHeading')}
          </h3>
          <p className="text-sm text-gray-500 mb-5">{t('home.earnings.calcHint')}</p>
          <div className="grid sm:grid-cols-3 gap-4">
            {EXAMPLE_MERCHANTS.map((count) => (
              <div key={count} className="rounded-lg bg-gray-50 border border-gray-200 p-5 text-center">
                <p className="text-sm text-gray-600 mb-1">
                  {t('home.earnings.calcMerchants', { count })}
                </p>
                <p className="text-3xl font-bold text-[#024ab0]">
                  <Amount value={perMerchant === undefined ? undefined : perMerchant * count} /> €
                </p>
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 mt-5">{t('home.earnings.calcFootnote')}</p>
          <p className="text-sm text-gray-500 mt-2">{t('home.earnings.condition')}</p>
        </div>
      </section>

      {/* 2. Wie einfach ist der Job? */}
      <section className="bg-gray-50 border-y border-gray-200">
        <div className="max-w-5xl mx-auto px-4 py-16">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-3">
            {t('home.job.heading')}
          </h2>
          <p className="text-lg text-center text-gray-600 max-w-2xl mx-auto mb-10">
            {t('home.job.intro')}
          </p>

          <div className="grid md:grid-cols-2 gap-5 mb-10">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="rounded-xl bg-white border border-gray-200 p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{t(`home.job.q${i}`)}</h3>
                <p className="text-gray-600">{t(`home.job.a${i}`)}</p>
              </div>
            ))}
          </div>

          <div className="rounded-xl bg-white border border-gray-200 p-7">
            <h3 className="font-semibold text-gray-900 mb-4 text-center">
              {t('home.trust.heading')}
            </h3>
            <ul className="grid sm:grid-cols-2 gap-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <li key={i} className="flex items-start gap-2.5 text-gray-700">
                  <CheckIcon className="w-5 h-5 text-[#107bd4] shrink-0 mt-0.5" />
                  {t(`home.trust.item${i}`)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 3. Vier Schritte */}
      <section className="max-w-5xl mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">
          {t('home.steps.heading')}
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-xl border border-gray-200 p-6 bg-white">
              <div className="w-10 h-10 rounded-full bg-[#107bd4] text-white flex items-center justify-center font-bold mb-4">
                {i}
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">{t(`home.steps.step${i}Title`)}</h3>
              <p className="text-sm text-gray-600">{t(`home.steps.step${i}Text`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 4. Jetzt bewerben */}
      <section className="bg-[#024ab0]">
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">{t('home.cta.heading')}</h2>
          <p className="text-lg text-blue-100 mb-8">{t('home.cta.text')}</p>
          <a
            href="/register"
            className="inline-block bg-[#ff5801] hover:bg-[#e64f00] text-white font-semibold rounded-lg px-10 py-4 text-lg transition-colors shadow-lg"
          >
            {t('home.cta.button')}
          </a>
          <p className="text-blue-100 mt-6 text-sm">
            {t('home.cta.loginPrefix')}{' '}
            <a href="/login" className="underline font-semibold hover:text-white">
              {t('home.login')}
            </a>
          </p>
        </div>
      </section>

      {/* 5. Erst danach: Videos und Hintergrund zur Plattform */}
      <section className="max-w-4xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
          {t('home.videos.heading')}
        </h2>
        <p className="text-center text-gray-600 mb-8">{t('home.videos.text')}</p>
        <div className="space-y-6">
          {['xjn_N1eMGY4', 'IT0DtJf9U98', '50fY-EJ5Y3k'].map((videoId, index) => (
            <YouTubeLazy
              key={videoId}
              videoId={videoId}
              title={`${t('home.explainerVideo')} ${index + 1}`}
              onPlay={pauseMusic}
            />
          ))}
        </div>

        <details className="mt-10 rounded-xl border border-gray-200 bg-gray-50 p-6">
          <summary className="font-semibold text-gray-900 cursor-pointer">
            {t('home.background.heading')}
          </summary>
          <p className="text-gray-600 mt-4 leading-relaxed">{t('home.background.text')}</p>
        </details>
      </section>
    </div>
  )
}
